import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import App from "./App.tsx";
import { ErrorBoundary, AuthProvider } from "@/components";
import ServerError from "@/components/error/ServerError";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { FindProvider } from "@/contexts/FindContext";
import { OptimizedAuthProvider } from "@/contexts/OptimizedAuthContext";
import { FiltersProvider } from "@/contexts/FiltersContext";
import { ResponsiveProvider } from "@/contexts/ResponsiveContext";
import { ServerErrorProvider } from "@/contexts/ServerErrorContext";
import { ToastProvider } from "@/contexts/ToastContext";
import { PostHogProvider } from "@/lib/analytics";
import { LazyMotion, domAnimation } from "framer-motion";
import * as Sentry from "@sentry/react";

Sentry.init({
  dsn: "https://a9f1065b56b738ad66d737ae646390e3@o4509210106068992.ingest.us.sentry.io/4509877944123392",
  environment: import.meta.env.MODE,
  sendDefaultPii: true,
  enabled: import.meta.env.MODE !== "development",
});

// When a lazy chunk fails to load — almost always a stale chunk after a deploy
// changed the hashed filenames (the old file is 404 on the CDN) — reload once to
// pick up the new build instead of throwing "Failed to fetch dynamically
// imported module". Guarded by a short cooldown so a genuinely missing chunk
// can't loop: if it fails again within the window, we let the error surface.
window.addEventListener("vite:preloadError", () => {
  const KEY = "vite:preloadReloadAt";
  const last = Number(sessionStorage.getItem(KEY) || 0);
  if (Date.now() - last < 10_000) return; // reloaded just now → don't loop
  sessionStorage.setItem(KEY, String(Date.now()));
  window.location.reload();
});

// Debug environment variables
// console.log("Environment check:", {
//   VITE_CLERK_PUBLISHABLE_KEY: import.meta.env.VITE_CLERK_PUBLISHABLE_KEY,
//   MODE: import.meta.env.MODE,
// });

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 2, // 2 minutes (reduced from 5)
      gcTime: 1000 * 60 * 10, // 10 minutes
      refetchOnMount: false, // Don't refetch on mount if data is fresh
      refetchOnWindowFocus: false, // Don't refetch on window focus
      refetchOnReconnect: false, // Don't refetch on reconnect
    },
  },
});

const root = createRoot(document.getElementById("root")!, {
  // Callback called when an error is thrown and not caught by an ErrorBoundary.
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    console.warn("Uncaught error", error, errorInfo.componentStack);
  }),
  // Callback called when React catches an error in an ErrorBoundary.
  onCaughtError: Sentry.reactErrorHandler(),
  // Callback called when React automatically recovers from errors.
  onRecoverableError: Sentry.reactErrorHandler(),
});

try {
  root.render(
    <StrictMode>
      <ErrorBoundary>
        {/* LazyMotion + `m` components load only the `domAnimation` feature set
            (much smaller than the full motion engine) once for the whole tree.
            `strict` makes any stray `motion.*` throw in dev so nothing silently
            keeps the heavy bundle. */}
        <LazyMotion features={domAnimation} strict>
          <ResponsiveProvider>
          <ServerErrorProvider>
            <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <OptimizedAuthProvider>
                <PostHogProvider>
                  <ToastProvider>
                    <FindProvider>
                      <FiltersProvider>
                        <App />
                      </FiltersProvider>
                    </FindProvider>
                  </ToastProvider>
                </PostHogProvider>
              </OptimizedAuthProvider>
            </AuthProvider>
            </QueryClientProvider>
          </ServerErrorProvider>
          </ResponsiveProvider>
        </LazyMotion>
      </ErrorBoundary>
    </StrictMode>
  );
} catch (error) {
  console.error("Failed to render app:", error);
  root.render(<ServerError error={error as Error} />);
}
